import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

const PART_LABELS = {
  mcq: 'MCQ Section',
  essay: 'Essay Section',
  structure: 'Structured Section'
};

const ORANGE_BUTTON_SX = {
  background: 'linear-gradient(90deg, #FF8B4A 0%, #FF6244 100%)',
  color: '#ffffff',
  textTransform: 'none',
  fontFamily: 'Poppins',
  fontSize: '12px',
  fontWeight: 600,
  borderRadius: '999px',
  px: 2,
  py: 0.75,
  minWidth: 96,
  boxShadow: '0 6px 16px rgba(255, 98, 68, 0.28)',
  '&:hover': {
    background: 'linear-gradient(90deg, #ff9b5f 0%, #ff7357 100%)',
    boxShadow: '0 8px 20px rgba(255, 98, 68, 0.35)'
  },
  '&.Mui-disabled': {
    background: '#e2e8f0',
    color: '#94a3b8',
    boxShadow: 'none'
  }
};

const COMPLETED_TAG_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  px: 1.75,
  py: 0.6,
  borderRadius: '999px',
  bgcolor: '#f0fdf4',
  border: '1.5px solid #86efac',
  color: '#16a34a',
  fontSize: '12px',
  fontWeight: 600,
  fontFamily: 'Poppins',
  flexShrink: 0
};

const getPartStatus = (part) => {
  if (part.hasAttended) return 'Completed';
  if (part.isGrading) return 'Grading';
  if (part.inProgress) return 'In progress';
  return 'Not started';
};

const getPartActionLabel = (part) => {
  if (part.inProgress) return 'Resume';
  return 'Start';
};

const ResumeMixedExamModal = ({ isOpen, onClose, exam, onSelectPart }) => {
  if (!exam) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose(event, reason);
        }
      }}
      disableScrollLock
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(15, 23, 42, 0.4)',
          backdropFilter: 'blur(8px)'
        },
        '& .MuiPaper-root': {
          borderRadius: '24px !important',
          width: '100%',
          maxWidth: '480px',
          margin: '16px',
          boxShadow: '0 20px 40px -15px rgba(0, 0, 0, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.8)',
          fontFamily: "'Poppins', sans-serif",
          overflow: 'visible',
          position: 'relative',
          p: { xs: 3, sm: 4 },
          pt: { xs: 4, sm: 5 }
        }
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 20,
          top: 20,
          color: '#ef4444',
          bgcolor: '#ffffff',
          border: '1.5px solid #fee2e2',
          boxShadow: '0 4px 12px rgba(239, 68, 68, 0.08)',
          transition: 'all 0.25s',
          width: 36,
          height: 36,
          zIndex: 1,
          '&:hover': {
            color: '#dc2626',
            backgroundColor: '#fee2e2',
            transform: 'rotate(90deg)'
          }
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </IconButton>

      <DialogContent sx={{ p: 0 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 750,
            color: '#1e293b',
            mb: 1,
            fontSize: { xs: '20px', sm: '22px' },
            fontFamily: "'Poppins', sans-serif",
            textAlign: 'center'
          }}
        >
          Choose a section
        </Typography>

        <Typography
          sx={{
            fontSize: '13.5px',
            color: '#64748b',
            fontFamily: "'Poppins', sans-serif",
            lineHeight: 1.5,
            mb: 3,
            textAlign: 'center',
            px: 1
          }}
        >
          This exam has multiple sections — pick which one to continue.
        </Typography>

        <Stack spacing={1.5}>
          {(exam.childExamsStatus || []).map((part) => {
            const label = PART_LABELS[part.examType] || part.examType;
            const isCompleted = part.hasAttended;

            return (
              <Box
                key={part.examId}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  border: '1.5px solid #e2e8f0',
                  borderRadius: '16px',
                  p: 2,
                  bgcolor: '#fafbfc',
                  transition: 'border-color 0.2s ease',
                  '&:hover': {
                    borderColor: '#cbd5e1'
                  }
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 650,
                      fontSize: '14px',
                      fontFamily: 'Poppins',
                      color: '#1e293b',
                      mb: 0.25
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: '#94a3b8',
                      fontFamily: 'Poppins'
                    }}
                  >
                    {getPartStatus(part)}
                  </Typography>
                </Box>

                {isCompleted ? (
                  <Box sx={COMPLETED_TAG_SX}>
                    <CheckCircleRoundedIcon sx={{ fontSize: 16 }} />
                    Completed
                  </Box>
                ) : (
                  <Button
                    size="small"
                    variant="contained"
                    disabled={part.isGrading}
                    startIcon={<PlayArrowRoundedIcon sx={{ fontSize: 16 }} />}
                    onClick={() => onSelectPart(part)}
                    sx={ORANGE_BUTTON_SX}
                  >
                    {getPartActionLabel(part)}
                  </Button>
                )}
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ResumeMixedExamModal;
